import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormHelperText,
  Grid,
  InputLabel,
  makeStyles,
  Slider,
  Typography,
  useTheme,
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'
import { validators } from '../../validation/validators'
import { Dialog } from '../dialog/Dialog'
import { ControlledTextField } from '../form/ControlledTextField'
import { FormGroup } from '../form/FormGroup'
import { TemperatureCurveChart } from './TemperatureCurveChart'

export type TemperatureCurveRow = {
  rowId: string
  outdoor: string
  in: string
  return: string
}
export type TemperatureCurveFormValues = {
  id?: string
  name: string
  table: TemperatureCurveRow[]
}

type Props = {
  isCreate: boolean
  isOpen: boolean
  handleClose: () => void
  onSubmit: (values: TemperatureCurveFormValues) => void
  onDelete: (values: TemperatureCurveFormValues) => void
  defaultValues: TemperatureCurveFormValues
}

const useStyles = makeStyles((theme) => ({
  label: {
    flexGrow: 1,
    textAlign: 'center',
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
}))

export const DEFAULT_ROWCOUNT = 4
export const createDefaultRowValues = (): TemperatureCurveRow => ({
  rowId: uuid(), // Needed for react-hook-form
  outdoor: '',
  in: '',
  return: '',
})

export function TemperatureCurveDialog({
  isCreate,
  isOpen,
  handleClose,
  onSubmit,
  onDelete,
  defaultValues,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const classes = useStyles()
  const theme = useTheme()

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
  } = useForm<TemperatureCurveFormValues>({
    defaultValues,
  })

  const [rowCount, setRowCount] = useState(
    defaultValues.table.length ?? DEFAULT_ROWCOUNT,
  )

  const { fields, append, remove } = useFieldArray<TemperatureCurveRow>({
    control,
    name: 'table',
  })

  useEffect(() => {
    if (defaultValues.table.length !== rowCount) {
      setRowCount(defaultValues.table.length)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues.table.length])

  useEffect(() => {
    if (rowCount > fields.length) {
      const rowsToAdd = rowCount - fields.length
      for (let index = 0; index < rowsToAdd; index++) {
        const values =
          defaultValues.table[fields.length + index] ?? createDefaultRowValues()
        append(values)
      }
    }

    if (rowCount < fields.length) {
      const removeIndices = fields.map((_, i) => i).splice(rowCount)
      remove(removeIndices)
    }
  }, [rowCount, fields, append, remove, defaultValues.table])

  const title = isCreate
    ? t('temperatureCurve.dialog.title')
    : t('temperatureCurve.dialog.edit')

  const tableValues = watch('table')

  const mapToChartValues = (row: TemperatureCurveRow) => ({
    outdoor: row.outdoor ?? '0',
    in: row.in ?? '0',
    return: row.return ?? '0',
  })

  return (
    <Dialog title={title} open={isOpen} onClose={handleClose} maxWidth="md">
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container>
            <Grid item xs={12} md={6}>
              <FormGroup display="flex" flexDirection="row" gridGap={20}>
                <Box width="70%">
                  <ControlledTextField
                    name="name"
                    control={control}
                    label={t('temperatureCurve.dialog.name')}
                    rules={{ ...validators.required }}
                    error={errors.name}
                  />
                </Box>

                <Box width="30%" paddingX={1}>
                  <Typography>
                    {t('temperatureCurve.dialog.rowCount')}
                  </Typography>
                  <Slider
                    defaultValue={rowCount}
                    min={2}
                    max={6}
                    marks={[2, 3, 4, 5, 6].map((value) => ({
                      value,
                      label: String(value),
                    }))}
                    onChangeCommitted={(_, value) => {
                      setRowCount(Number(value))
                    }}
                  />
                </Box>
              </FormGroup>
              <FormGroup>
                <Box display="flex" flexDirection="row" width="100%">
                  <InputLabel className={classes.label}>
                    {t('temperatureCurve.dialog.outdoorTemperature')}
                  </InputLabel>
                  <InputLabel className={classes.label}>
                    {t('temperatureCurve.dialog.inTemperature')}
                  </InputLabel>
                  <InputLabel className={classes.label}>
                    {t('temperatureCurve.dialog.returnTemperature')}
                  </InputLabel>
                </Box>
                {fields.map((field, index) => (
                  <Box
                    display="flex"
                    flexDirection="row"
                    key={field.rowId}
                    gridGap={25}
                    paddingBottom={1.5}
                  >
                    <ControlledTextField
                      control={control}
                      name={`table.${index}.outdoor`}
                      defaultValue={field.outdoor}
                      rules={{ ...validators.required }}
                    />
                    <ControlledTextField
                      control={control}
                      name={`table.${index}.in`}
                      defaultValue={field.in}
                      rules={{ ...validators.required }}
                    />
                    <ControlledTextField
                      control={control}
                      name={`table.${index}.return`}
                      defaultValue={field.return}
                      rules={{ ...validators.required }}
                    />
                  </Box>
                ))}
                {isDirty && errors.table && (
                  <FormHelperText error>
                    {t('validation.temperatureCurve')}
                  </FormHelperText>
                )}
              </FormGroup>
            </Grid>
            <Grid item xs={12} md={6}>
              <TemperatureCurveChart
                table={tableValues.map(mapToChartValues)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box display="flex" flexGrow={1} gridGap={theme.spacing(1)}>
            {!isCreate && (
              <Button
                data-cy="delete-temperature-curve"
                variant="contained"
                color="secondary"
                onClick={() => onDelete(defaultValues)}
              >
                {t('common.remove')}
              </Button>
            )}

            <Box flexGrow={1} />
            <Button variant="contained" onClick={handleClose}>
              {t('common.cancel')}
            </Button>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={
                !isDirty || Boolean(errors.table) || Boolean(errors.name)
              }
            >
              {isCreate ? t('common.create') : t('common.saveChanges')}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  )
}
